import { Link } from "@tanstack/react-router";
import { clsx } from "clsx";
import type { CatalogIndexBy } from "@/api/types";

/**
 * Indexing-scheme toggle for the catalog entrance (Part 5): by institution
 * (default), by authority, by subject. URL-driven via `?index_by=` so a
 * deep link reconstructs the exact view. Anatomy mirrors ScopeToggle so the
 * segmented controls read as siblings across surfaces.
 */
export const INDEX_BY_VALUES: readonly CatalogIndexBy[] = [
  "institution",
  "authority",
  "subject",
] as const;

const LABEL: Record<CatalogIndexBy, string> = {
  institution: "By institution",
  authority: "By authority",
  subject: "By subject",
};

export interface IndexingToggleProps {
  active: CatalogIndexBy;
}

export function IndexingToggle({ active }: IndexingToggleProps) {
  return (
    <nav
      aria-label="Catalog indexing scheme"
      className="inline-flex rounded-full bg-surface-sunken p-1"
    >
      {INDEX_BY_VALUES.map((target) => {
        const isActive = target === active;
        return (
          <Link
            key={target}
            to="/"
            search={{ mode: "catalog", index_by: target }}
            aria-current={isActive ? "page" : undefined}
            className={clsx(
              "rounded-full px-4 py-1.5 text-meta font-medium transition",
              isActive
                ? "bg-surface-card text-ink-primary shadow-sm"
                : "text-ink-secondary hover:text-ink-primary",
            )}
          >
            {LABEL[target]}
          </Link>
        );
      })}
    </nav>
  );
}
